import { useState } from "react";
import { FileJson, FileText, Image, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useConfigStore } from "../../../store/configStore";
import { useEditorStore } from "../../../store/editorStore";
import { UploadArea } from "../components/UploadArea";
import {
  parseSpineToConfig,
  validateSpineJson,
  validateSkinNames,
  readFileAsText,
  readFileAsDataUrl,
  readFileAsArrayBuffer,
  detectAtlasRegionMismatch,
  fixAtlasTexts,
  type AtlasMismatchInfo,
} from "../../../lib/spineParser";
import { PendingSpineData } from "../types";

// 读取并校验 Spine 文件，返回解析后的数据（不含任何副作用）
export const parseSpineFiles = async (
  files: File[],
): Promise<PendingSpineData | null> => {
  const fileList = Array.from(files);
  const jsonFile = fileList.find((f) => f.name.toLowerCase().endsWith(".json"));
  const skelFile = fileList.find((f) => f.name.toLowerCase().endsWith(".skel"));
  const atlasFiles = fileList.filter((f) => f.name.toLowerCase().endsWith(".atlas"));
  const pngFiles = fileList.filter((f) => f.name.toLowerCase().endsWith(".png"));

  // 必须有 json 或 skel 其中之一
  if (!jsonFile && !skelFile) {
    alert("请选择 .json 或 .skel 文件");
    return null;
  }
  if (atlasFiles.length === 0 || pngFiles.length === 0) {
    alert("请同时选择至少一个 .atlas 和至少一个 .png 文件");
    return null;
  }

  let jsonData: any;
  let jsonText: string = "";
  let skelBytes: Uint8Array | undefined;

  if (skelFile) {
    const arrayBuffer = await readFileAsArrayBuffer(skelFile);
    skelBytes = new Uint8Array(arrayBuffer);
    jsonData = {
      skeleton: {},
      skins: [],
      animations: {},
      slots: [],
      _fromSkel: true,
      _pendingSkelParse: true,
    };
  } else {
    jsonText = await readFileAsText(jsonFile!);
    jsonData = JSON.parse(jsonText);

    if (!validateSpineJson(jsonData)) {
      alert("无效的 Spine JSON 格式");
      return null;
    }

    const skinNameErrors = validateSkinNames(jsonData);
    if (skinNameErrors.length > 0) {
      const errorMsg = skinNameErrors
        .slice(0, 10)
        .map((e) => `• ${e.skinName}: ${e.errors.join(", ")}`)
        .join("\n");
      const more =
        skinNameErrors.length > 10 ? `\n... 还有 ${skinNameErrors.length - 10} 个错误` : "";
      alert(`Skin name 校验失败:\n\n${errorMsg}${more}`);
      return null;
    }
  }

  const atlasTexts: Record<string, string> = {};
  for (const atlasFile of atlasFiles) {
    const atlasText = await readFileAsText(atlasFile);
    atlasTexts[atlasFile.name] = atlasText;
  }

  const atlasPngNames: string[] = [];
  for (const [, atlasText] of Object.entries(atlasTexts)) {
    for (const line of atlasText.split("\n")) {
      const trimmed = line.trim();
      if (trimmed.endsWith(".png")) {
        atlasPngNames.push(trimmed);
      }
    }
  }

  const pngDataUrls: Record<string, string> = {};
  for (const atlasPngName of atlasPngNames) {
    const baseName = atlasPngName.split("/").pop() || atlasPngName;
    const matchedFile = pngFiles.find((f) => {
      const uploadBaseName = f.name.split("/").pop() || f.name;
      return uploadBaseName === baseName || uploadBaseName === atlasPngName;
    });

    if (matchedFile) {
      const dataUrl = await readFileAsDataUrl(matchedFile);
      pngDataUrls[atlasPngName] = dataUrl;
    }
  }

  const missingPngs = atlasPngNames.filter((name) => !pngDataUrls[name]);
  if (missingPngs.length > 0) {
    console.warn(`[Import] 以下 PNG 文件在 atlas 中被引用但未找到: ${missingPngs.join(", ")}`);
  }

  return { jsonData, jsonText, skelBytes, atlasTexts, pngDataUrls, atlasPngNames };
};

interface SpineUploaderProps {
  onSuccess: () => void;
  isStaticTemplate: boolean;
}

export function SpineUploader({ onSuccess, isStaticTemplate }: SpineUploaderProps) {
  const { templateConfig, setTemplateConfig, setSpineData } = useConfigStore();
  const { randomizeSkins } = useEditorStore();

  const [fixDialogOpen, setFixDialogOpen] = useState(false);
  const [atlasMismatches, setAtlasMismatches] = useState<AtlasMismatchInfo[]>([]);
  const [pendingSpineData, setPendingSpineData] = useState<PendingSpineData | null>(null);

  const checkAndPromptAtlasFix = (data: PendingSpineData): boolean => {
    const mismatches = detectAtlasRegionMismatch(data.jsonData, data.atlasTexts);
    if (mismatches.length > 0) {
      setPendingSpineData(data);
      setAtlasMismatches(mismatches);
      setFixDialogOpen(true);
      return false;
    }
    return true;
  };

  const finalizeSpineImport = (
    jsonData: any,
    jsonText: string,
    atlasTexts: Record<string, string>,
    pngDataUrls: Record<string, string>,
    skelBytes?: Uint8Array,
  ) => {
    const spineDataPayload: any = { json: jsonData, jsonText, atlasTexts, pngDataUrls };
    if (skelBytes) {
      spineDataPayload.skelBytes = skelBytes;
    }
    setSpineData(spineDataPayload);
    // 重新导入 Spine 源文件后，下次保存才需要考虑重新上传 Zip
    useEditorStore.getState().setSpineZipDirty(true);
    useEditorStore.getState().setIsDirty(true);

    const baseConfig = parseSpineToConfig(jsonData);
    if (templateConfig) {
      setTemplateConfig({
        ...templateConfig,
        animations: baseConfig.animations,
      });
    } else {
      setTemplateConfig(baseConfig);
      setTimeout(() => randomizeSkins(), 0);
    }

    setFixDialogOpen(false);
    setPendingSpineData(null);
    setAtlasMismatches([]);
    onSuccess();
  };

  const handleAtlasFixConfirm = () => {
    if (!pendingSpineData) return;
    const { jsonData, jsonText, atlasTexts, pngDataUrls, skelBytes } = pendingSpineData;
    const fixedAtlasTexts = fixAtlasTexts(atlasTexts, atlasMismatches);
    finalizeSpineImport(jsonData, jsonText, fixedAtlasTexts, pngDataUrls, skelBytes);
  };

  const handleImportSpine = async (files: File[]) => {
    if (isStaticTemplate) {
      alert("静态模版（前景/背景静态）不支持导入 Spine 资源文件");
      return;
    }
    try {
      const data = await parseSpineFiles(files);
      if (!data) return;
      if (!checkAndPromptAtlasFix(data)) return;

      finalizeSpineImport(
        data.jsonData,
        data.jsonText,
        data.atlasTexts,
        data.pngDataUrls,
        data.skelBytes,
      );
    } catch (err) {
      alert(`导入失败: ${err instanceof Error ? err.message : "未知错误"}`);
    }
  };

  return (
    <>
      <div className="flex-1 min-h-0 flex flex-col h-full">
        {templateConfig && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 shrink-0">
            <div className="flex gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-800">
                导入新的 Spine 文件将更新 Spine 数据和动画列表，保留现有的 Selections 和 Tonings 配置。
              </p>
            </div>
          </div>
        )}
        <UploadArea
          icon={
            <div className="flex items-center justify-center gap-2 mb-4">
              <FileJson className="h-8 w-8 text-blue-500" />
              <FileText className="h-8 w-8 text-amber-500" />
              <Image className="h-8 w-8 text-green-500" />
            </div>
          }
          description="拖拽文件到此处上传"
          fileHint="支持 .json/.skel、.atlas 和 .png 文件"
          onDrop={handleImportSpine}
        />
      </div>

      <Dialog
        open={fixDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setFixDialogOpen(false);
            setPendingSpineData(null);
            setAtlasMismatches([]);
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Atlas Region 路径前缀缺失
            </DialogTitle>
            <DialogDescription>
              检测到多纹理 Atlas 文件中的 region 名称缺少路径前缀，与 Spine JSON 中的 attachment
              名称不匹配。点击「一键修复」将自动为 region 添加正确的路径前缀后继续导入。
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="flex-1 min-h-0 max-h-[400px] overflow-y-auto">
            <div className="space-y-4 pr-4">
              {atlasMismatches.map((mismatch) => (
                <div key={mismatch.atlasFileName} className="space-y-2">
                  <h4 className="font-medium text-sm text-amber-600 bg-amber-50 p-2 rounded">
                    文件: {mismatch.atlasFileName}
                  </h4>
                  <ul className="space-y-2 pl-2">
                    {mismatch.missingRegions.map((region) => (
                      <li key={region.original} className="text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-red-500 line-through shrink-0">
                            {region.original}
                          </span>
                          <span className="text-muted-foreground shrink-0">→</span>
                          <span className="text-green-600 font-mono break-all">
                           {region.fixed}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </ScrollArea>

          <DialogFooter className="shrink-0 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setFixDialogOpen(false);
                setPendingSpineData(null);
                setAtlasMismatches([]);
              }}
            >
              取消并终止导入
            </Button>
            <Button onClick={handleAtlasFixConfirm} className="gap-2">
              一键修复并继续导入
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
